import { contact, fieldNotes, futureUpdates, hero, navigation, organization, principles, story } from '../constants';

function Header() {
  return (
    <header className="sticky top-0 z-30 border-b border-root/10 bg-sunwash/85 backdrop-blur-xl">
      <nav className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 sm:px-8" aria-label="Main navigation">
        <a href="#home" className="flex items-center gap-3 font-black text-soil">
          <span className="grid h-12 w-12 rotate-[-8deg] place-items-center rounded-[1.35rem] bg-clay text-base text-sunwash shadow-paper">SH</span>
          <span className="hidden text-sm uppercase tracking-[0.24em] sm:inline">{organization.shortName}</span>
        </a>
        <div className="hidden items-center gap-1 md:flex">
          {navigation.map((item) => (
            <a key={item.href} href={item.href} className="rounded-full px-4 py-2 text-sm font-black text-soil/68 transition hover:bg-petal hover:text-soil">
              {item.label}
            </a>
          ))}
        </div>
      </nav>
    </header>
  );
}

function SeedMark() {
  return (
    <div className="relative h-64 rounded-[3rem] bg-soil p-5 shadow-seedling sm:h-80">
      <div className="absolute inset-4 rounded-[2.4rem] border border-sunwash/20 bg-seed-lines" />
      <div className="absolute bottom-8 left-8 right-8 rounded-[2rem] bg-sunwash p-6 text-soil shadow-paper">
        <p className="text-xs font-black uppercase tracking-[0.24em] text-clay">Verified public address</p>
        <address className="mt-4 not-italic font-display text-2xl font-black leading-tight">{organization.address}</address>
      </div>
      <div className="absolute right-9 top-8 h-24 w-16 rounded-[90%_10%_90%_10%] bg-sprout" />
      <div className="absolute right-24 top-20 h-20 w-12 rounded-[10%_90%_10%_90%] bg-leaf" />
    </div>
  );
}

function Hero() {
  return (
    <section id="home" className="relative isolate overflow-hidden bg-dawn-field px-5 py-16 sm:px-8 lg:py-24">
      <div className="absolute inset-x-0 bottom-0 -z-10 h-24 bg-gradient-to-t from-sunwash to-transparent" />
      <div className="mx-auto grid max-w-7xl gap-10 lg:grid-cols-[0.95fr_1.05fr] lg:items-end">
        <div className="lg:pb-10">
          <p className="inline-flex rounded-full bg-soil px-4 py-2 text-xs font-black uppercase tracking-[0.28em] text-sunwash shadow-paper">{hero.eyebrow}</p>
          <h1 className="mt-8 max-w-5xl font-display text-5xl font-black leading-[0.96] text-soil sm:text-7xl lg:text-8xl">{hero.title}</h1>
        </div>
        <div className="grid gap-5">
          <p className="rounded-[2rem] border border-root/10 bg-sunwash/75 p-6 text-xl leading-9 text-soil/76 shadow-paper sm:p-8">{hero.body}</p>
          <div className="grid gap-3 sm:grid-cols-2">
            <a href={hero.primaryCta.href} className="rounded-full bg-clay px-7 py-4 text-center text-sm font-black uppercase tracking-[0.18em] text-sunwash shadow-paper transition hover:-translate-y-0.5 hover:bg-root">
              {hero.primaryCta.label}
            </a>
            <a href={hero.secondaryCta.href} className="rounded-full border border-root/20 bg-sunwash/60 px-7 py-4 text-center text-sm font-black uppercase tracking-[0.18em] text-soil transition hover:-translate-y-0.5 hover:bg-seed">
              {hero.secondaryCta.label}
            </a>
          </div>
        </div>
      </div>
      <div className="mx-auto mt-12 max-w-7xl">
        <SeedMark />
      </div>
    </section>
  );
}

function FieldNotes() {
  return (
    <section aria-label="Foundation facts" className="px-5 py-10 sm:px-8">
      <div className="mx-auto grid max-w-7xl gap-4 md:grid-cols-3">
        {fieldNotes.map((note) => (
          <article key={note.label} className="rounded-[2rem] border border-root/10 bg-sunwash p-6 shadow-paper">
            <p className="text-xs font-black uppercase tracking-[0.24em] text-clay">{note.label}</p>
            <p className="mt-3 font-display text-2xl font-black text-soil">{note.value}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

function StorySection() {
  return (
    <section id="story" className="px-5 py-20 sm:px-8">
      <div className="mx-auto grid max-w-7xl gap-8 lg:grid-cols-[0.55fr_1.45fr]">
        <div className="min-h-64 rounded-[3rem] bg-seed-lines bg-petal p-6">
          <div className="h-full rounded-[2.25rem] border border-root/10 bg-sunwash/70" />
        </div>
        <div className="rounded-[3rem] bg-soil p-8 text-sunwash shadow-seedling sm:p-12">
          <p className="text-sm font-black uppercase tracking-[0.28em] text-marigold">{story.eyebrow}</p>
          <h2 className="mt-5 font-display text-4xl font-black leading-tight sm:text-6xl">{story.title}</h2>
          <p className="mt-7 max-w-3xl text-lg leading-9 text-sunwash/75">{story.body}</p>
        </div>
      </div>
    </section>
  );
}

function PrinciplesSection() {
  return (
    <section id="principles" className="bg-sky px-5 py-20 sm:px-8">
      <div className="mx-auto max-w-7xl">
        <p className="text-sm font-black uppercase tracking-[0.28em] text-clay">Principles</p>
        <h2 className="mt-4 max-w-4xl font-display text-4xl font-black leading-tight text-soil sm:text-6xl">Built like seed rows: simple, visible, ready to grow.</h2>
        <div className="mt-10 grid gap-5 lg:grid-cols-3">
          {principles.map((principle, index) => (
            <article key={principle.title} className="rounded-[2.5rem] bg-sunwash p-7 shadow-paper">
              <p className="text-sm font-black text-clay">Row {index + 1}</p>
              <h3 className="mt-5 font-display text-2xl font-black text-soil">{principle.title}</h3>
              <p className="mt-4 leading-7 text-soil/70">{principle.body}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function FutureSection() {
  return (
    <section id="future" className="px-5 py-20 sm:px-8">
      <div className="mx-auto grid max-w-7xl gap-8 lg:grid-cols-[1fr_0.85fr] lg:items-center">
        <div>
          <p className="text-sm font-black uppercase tracking-[0.28em] text-clay">Future updates</p>
          <h2 className="mt-4 font-display text-4xl font-black leading-tight text-soil sm:text-6xl">Room for the next approved details.</h2>
        </div>
        <ul className="grid gap-3">
          {futureUpdates.map((item) => (
            <li key={item} className="rounded-full border border-root/10 bg-seed px-6 py-4 font-black text-soil shadow-paper">{item}</li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function ContactFooter() {
  return (
    <footer id="contact" className="bg-root px-5 py-16 text-sunwash sm:px-8">
      <div className="mx-auto grid max-w-7xl gap-10 lg:grid-cols-[0.9fr_1.1fr]">
        <div>
          <p className="text-sm font-black uppercase tracking-[0.28em] text-marigold">{contact.eyebrow}</p>
          <h2 className="mt-4 font-display text-4xl font-black leading-tight sm:text-6xl">{contact.title}</h2>
          <p className="mt-6 max-w-2xl text-lg leading-8 text-sunwash/72">{contact.body}</p>
        </div>
        <div className="rounded-[3rem] border border-sunwash/15 bg-sunwash/8 p-8">
          <p className="text-sm font-black uppercase tracking-[0.28em] text-marigold">Address</p>
          <address className="mt-5 not-italic font-display text-3xl font-black leading-tight">{organization.address}</address>
        </div>
      </div>
      <div className="mx-auto mt-12 flex max-w-7xl flex-col gap-3 border-t border-sunwash/12 pt-8 text-sm text-sunwash/60 sm:flex-row sm:items-center sm:justify-between">
        <p>{organization.name}</p>
        <p>Static MVP. No forms, donations, analytics, or personal data collection.</p>
      </div>
    </footer>
  );
}

export function SowingHopePage() {
  return (
    <div className="min-h-screen bg-sunwash text-ink">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-50 focus:rounded-full focus:bg-clay focus:px-5 focus:py-3 focus:text-sm focus:font-black focus:text-sunwash">
        Skip to content
      </a>
      <Header />
      <main id="main-content">
        <Hero />
        <FieldNotes />
        <StorySection />
        <PrinciplesSection />
        <FutureSection />
      </main>
      <ContactFooter />
    </div>
  );
}

export default SowingHopePage;
